import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchPatients } from '../../redux/slices/patientsSlice';


import { generateMRN } from '../../utils/mrnUtils';
import { isEmailUnique } from '../../utils/patientValidation';
import { calculateAge } from '../../utils/dateUtils';
import PatientBase from './PatientBase';
// Use service layer instead of direct Firestore access
import { createPatient, createPatientDocument } from '../../services/patientService';
import { useUserData } from '../../hooks/useUserData';



const PatientForm = ({ onSuccess, onCancel, initialFacilityId, embedded = false, onCreationStart, onCreationError }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //const defaultFacility = useSelector(selectDefaultFacility);

  const { defaultFacility } = useUserData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mrn: '',
    dateOfBirth: '', // Required field
    gender: '',
    contact: '',
    email: '',
    address: '',
    bloodType: '',
    facilityId: initialFacilityId || defaultFacility?.id || '',
    allergies: '',
    insurance: '',
    policyNumber: '',
    primaryLanguage: '',
    status: 'Active',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  useEffect(() => {
    const facilityId = initialFacilityId || defaultFacility?.id;
    if (facilityId && !formData.facilityId) {
      setFormData(prev => ({
        ...prev,
        facilityId: facilityId
      }));
    }
  }, [initialFacilityId, defaultFacility, formData.facilityId]);

  const validateEmail = async (email) => {
    if (!email) return true;
    
    try {
      const isUnique = await isEmailUnique(email);
      if (!isUnique) {
        setErrors(prev => ({
          ...prev,
          email: 'Email is already in use by another patient'
        }));
        return false;
      }
      
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
      return true;
    } catch (error) {
      console.error('Error validating email:', error);
      return true; // Allow submission if validation fails
    }
  };

  // Check if patient is a minor (18 or younger)
  const isPatientMinor = () => {
    if (!formData.dateOfBirth) return false;
    return calculateAge(formData.dateOfBirth) <= 18;
  };





  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      
      if (onCreationStart) {
        onCreationStart();
      }

      // Ensure facilityId is set
      if (!formData.facilityId) {
        setErrors({ submit: 'Facility ID is required. Please select a facility.' });
        setIsSubmitting(false);
        return;
      }

      // Ensure date of birth is set
      if (!formData.dateOfBirth) {
        setErrors({ submit: 'Date of birth is required.' });
        setIsSubmitting(false);
        return;
      }

      // Validate email if provided (only for patients 18+)
      if (!isPatientMinor() && formData.email && !(await validateEmail(formData.email))) {
        setIsSubmitting(false);
        return;
      }

      // Generate MRN if not provided
      const finalMRN = formData.mrn || generateMRN();

      // Process allergies
      const allergiesList = formData.allergies 
        ? formData.allergies.split(',').map(allergy => allergy.trim()).filter(Boolean)
        : [];

      // Determine if patient is 18 or younger
      const age = calculateAge(formData.dateOfBirth);
      const isMinor = age <= 18;

      const patientData = {
        ...formData,
        mrn: finalMRN,
        allergies: allergiesList,
        email: isMinor ? '' : (formData.email?.toLowerCase() || ''), // Email optional for minors
        isMinor: isMinor, // Add flag for minor status
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const newPatient = await createPatient(patientData);
      const newPatientId = newPatient.id;

      // Handle uploaded documents if any exist
      if (formData.uploadedDocuments && formData.uploadedDocuments.length > 0) {
        try {
          const documentPromises = formData.uploadedDocuments.map(async (tempDoc) => {
            // Update the document metadata with the real patient ID
            const updatedDoc = {
              ...tempDoc,
              patientId: newPatientId,
              uploadedAt: new Date()
            };
            
            // Remove temporary flags
            delete updatedDoc.isTemporary;
            
            // Save document metadata to Firestore
            await createPatientDocument(updatedDoc);
          });

          await Promise.all(documentPromises);
          console.log(`Saved ${formData.uploadedDocuments.length} documents for patient ${newPatientId}`);
        } catch (docError) {
          console.error('Error saving documents:', docError);
          // Don't fail the patient creation if document saving fails
        }
      }
      
      // Refresh patients list
      dispatch(fetchPatients(formData.facilityId));
      
      if (onSuccess) {
        onSuccess({
          id: newPatientId,
          ...patientData
        });
      } else if (!embedded) {
        navigate('/patients');
      }
      
    } catch (error) {
      console.error('Error creating patient:', error);
      if (onCreationError) {
        onCreationError(error);
      }
      setErrors({ submit: 'Failed to create patient. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else if (!embedded) {
      navigate('/patients');
    }
  };

  return (
    <>
      {errors.submit && (
        <div style={{ color: 'red', marginBottom: '16px', textAlign: 'center' }}>
          {errors.submit}
        </div>
      )}
      <PatientBase
        mode="create"
        patient={null}
        encounters={[]}
        prescriptions={[]}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        onBack={handleBack}
        loading={false}
        saving={isSubmitting}
        title="Add New Patient"
        subtitle="Create a new patient record with complete information"
        showActions={true}
        facilityId={formData.facilityId}
        isMinor={isPatientMinor()}
      />
    </>
  );
};

export default PatientForm;
